// src/pages/gate/GatePreApprovals.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./GatePreApprovals.css";

export default function GatePreApprovals() {
  const navigate = useNavigate();
  const gateToken = localStorage.getItem("gateToken");
  const gateName = localStorage.getItem("gateName");

  const [preApprovals, setPreApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [checkedIn, setCheckedIn] = useState({});

  useEffect(() => {
    if (!gateToken) {
      navigate("/gate/login");
      return;
    }
    loadPreApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  function getTodayDate() {
    const today = new Date();
    return today.toISOString().split("T")[0];
  }

  const loadPreApprovals = async () => {
    try {
      setLoading(true);
      setError("");

      const API = process.env.REACT_APP_API_URL;
      const endpoint = selectedDate === getTodayDate()
        ? "/api/visitors/pre-approved-today"
        : `/api/visitors/pre-approved-by-date/${selectedDate}`;

      const response = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${gateToken}` }
      });

      if (!response.ok) throw new Error("Failed to fetch pre-approvals");

      const data = await response.json();
      setPreApprovals(data.preApprovals || []);
    } catch (err) {
      setError("Failed to load pre-approvals: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCheckedIn = (preApprovalId) => {
    setCheckedIn({
      ...checkedIn,
      [preApprovalId]: !checkedIn[preApprovalId]
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const getStats = () => {
    const total = preApprovals.length;
    const checked = Object.values(checkedIn).filter(Boolean).length;
    const pending = total - checked;
    return { total, checked, pending };
  };

  const stats = getStats();

  return (
    <div className="gate-layout">
      <div className="gate-header">
        <div className="header-left">
          <h1>⭐ Pre-Approved Visitors</h1>
          <p>Gate Staff: {gateName}</p>
        </div>
        <button
          className="btn-logout"
          onClick={() => {
            localStorage.removeItem("gateToken");
            localStorage.removeItem("gateName");
            localStorage.removeItem("gateShiftId");
            navigate("/gate/login");
          }}
        >
          Logout
        </button>
      </div>

      <div className="preapprovals-container">
        {/* Top Controls */}
        <div className="top-controls">
          <div className="date-selector">
            <label htmlFor="dateFilter">Select Date:</label>
            <input
              type="date"
              id="dateFilter"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
          </div>
          <button
            className="btn-enter-new-visitor"
            onClick={() => {
              // Clear pre-approval data if going to new visitor form
              sessionStorage.removeItem("preApprovedVisitor");
              navigate("/gate/visitor-entry");
            }}
          >
            📝 Enter New Visitor (Guest/Vendor)
          </button>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card total">
            <div className="stat-label">Total Expected</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card checked">
            <div className="stat-label">Checked In</div>
            <div className="stat-value">{stats.checked}</div>
          </div>
          <div className="stat-card pending">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Pre-Approvals List */}
        <div className="preapprovals-list">
          {loading ? (
            <div className="loading">Loading pre-approvals...</div>
          ) : preApprovals.length === 0 ? (
            <div className="empty-state">
              <p>No pre-approved visitors for {formatDate(selectedDate)}</p>
              <p className="small">Residents can pre-approve visitors from their dashboard</p>
            </div>
          ) : (
            preApprovals.map((approval) => (
              <div
                key={approval.id}
                className={`approval-card ${checkedIn[approval.id] ? "checked-in" : ""}`}
              >
                <div className="card-header">
                  <div className="visitor-info">
                    <h3>{approval.visitorName}</h3>
                    {checkedIn[approval.id] && (
                      <span className="badge checked">✓ Checked In</span>
                    )}
                  </div>
                  <div className="check-button">
                    <button
                      className={`btn-check ${checkedIn[approval.id] ? "checked" : ""}`}
                      onClick={() => handleMarkCheckedIn(approval.id)}
                      title={checkedIn[approval.id] ? "Mark as not checked in" : "Mark as checked in"}
                    >
                      {checkedIn[approval.id] ? "✓ Checked In" : "Check In"}
                    </button>
                  </div>
                </div>

                <div className="card-details">
                  {approval.visitorPhone && (
                    <div className="detail-row">
                      <span className="label">Phone:</span>
                      <span className="value">{approval.visitorPhone}</span>
                    </div>
                  )}

                  {approval.visitorEmail && (
                    <div className="detail-row">
                      <span className="label">Email:</span>
                      <span className="value">{approval.visitorEmail}</span>
                    </div>
                  )}

                  {approval.purpose && (
                    <div className="detail-row">
                      <span className="label">Purpose:</span>
                      <span className="value">{approval.purpose}</span>
                    </div>
                  )}

                  <div className="detail-row">
                    <span className="label">Expected Date:</span>
                    <span className="value">{formatDate(approval.expectedDate)}</span>
                  </div>

                  {approval.validUntilDate && approval.validUntilDate !== approval.expectedDate && (
                    <div className="detail-row">
                      <span className="label">Valid Until:</span>
                      <span className="value">{formatDate(approval.validUntilDate)}</span>
                    </div>
                  )}
                </div>

                <div className="card-action">
                  <button
                    className="btn-enter-visitor"
                    onClick={() => {
                      // Pass pre-approval data to visitor entry form
                      sessionStorage.setItem("preApprovedVisitor", JSON.stringify(approval));
                      navigate("/gate/visitor-entry");
                    }}
                  >
                    📝 Enter Visitor
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Info Box */}
        <div className="info-box">
          <h3>ℹ️ How to Use</h3>
          <ul>
            <li>View all pre-approved visitors for today or select another date</li>
            <li>Click "Check In" to mark a visitor as arrived</li>
            <li>Click "Enter Visitor" to create their visitor entry</li>
            <li>Pre-approved visitors skip the approval queue</li>
            <li>Residents can pre-approve visitors from their dashboard</li>
          </ul>
        </div>
      </div>
    </div>
  );
}